import React, { useState, useEffect, useCallback } from 'react';
import PersonalityAssessment from './PersonalityAssessment';
import UserMatching from './UserMatching';
import ConversationSuggestions from './ConversationSuggestions';
import ResultsVisualization from './ResultsVisualization';
import './ConversationEngine.css';

const ConversationEngine = ({ userId, userProfile, onProfileUpdate }) => {
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [personalityProfile, setPersonalityProfile] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [conversationSuggestions, setConversationSuggestions] = useState([]);
  const [environment, setEnvironment] = useState({
    venueType: 'upscale_cocktail_bar',
    crowdEnergy: 'moderate',
    noiseLevel: 'moderate',
    timeOfDay: 'evening',
    groupSize: 'small',
    formality: 'casual'
  });
  const [isLoading, setIsLoading] = useState(false);

  // Check if user has existing personality profile
  useEffect(() => {
    if (userProfile?.personalityAssessment) {
      setPersonalityProfile(userProfile.personalityAssessment);
      setCurrentScreen('results');
    }
  }, [userProfile]);

  const handleStartAssessment = () => {
    setCurrentScreen('assessment');
    setCurrentQuestionIndex(0);
    setAssessmentAnswers({});
  };

  const handleAssessmentComplete = (profile) => {
    setPersonalityProfile(profile);
    setCurrentScreen('results');
    // Save to parent component
    if (onProfileUpdate) {
      onProfileUpdate({
        personalityAssessment: profile,
        assessmentCompleted: true
      });
    }
  };

  const handleFindMatches = () => {
    setCurrentScreen('matching');
    // UserMatching component will handle the matching logic
  };

  const handleSelectMatch = (match) => {
    setSelectedMatch(match);
    setCurrentScreen('conversations');
  };

  const handleBackToResults = () => {
    setCurrentScreen('results');
    setSelectedMatch(null);
  };

  const handleRestartAssessment = () => {
    setPersonalityProfile(null);
    setAssessmentAnswers({});
    setCurrentQuestionIndex(0);
    setCurrentScreen('welcome');
  };

  const renderWelcomeScreen = () => (
    <div className="welcome-screen">
      <div className="text-center py-8">
        <h2 className="text-3xl font-bold text-white mb-4">
          ConversationEngine
        </h2>
        <p className="text-lg text-gray-300 mb-2">
          AI-Powered Social Assistant & Matching
        </p>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
          Transform social moments into meaningful connections with personalized 
          conversation suggestions and intelligent matching based on personality compatibility.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
          <div className="bg-gray-800 rounded-lg p-6">
            <span className="text-3xl mb-3 block">🧠</span>
            <h3 className="text-lg font-semibold text-white mb-2">Personality Analysis</h3>
            <p className="text-sm text-gray-400">Big Five model assessment</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <span className="text-3xl mb-3 block">🤝</span>
            <h3 className="text-lg font-semibold text-white mb-2">Smart Matching</h3>
            <p className="text-sm text-gray-400">Find compatible connections</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6">
            <span className="text-3xl mb-3 block">💬</span>
            <h3 className="text-lg font-semibold text-white mb-2">Conversation Starters</h3>
            <p className="text-sm text-gray-400">AI-generated suggestions</p>
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={handleStartAssessment}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Assessment
          </button>
          {personalityProfile && (
            <button
              onClick={() => setCurrentScreen('results')}
              className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
            >
              View My Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="conversation-engine-container">
      {currentScreen === 'welcome' && renderWelcomeScreen()}
      
      {currentScreen === 'assessment' && (
        <PersonalityAssessment
          onComplete={handleAssessmentComplete}
          onBack={() => setCurrentScreen('welcome')}
        />
      )}
      
      {currentScreen === 'results' && personalityProfile && (
        <ResultsVisualization
          profile={personalityProfile}
          onFindMatches={handleFindMatches}
          onRetakeAssessment={handleRestartAssessment}
        />
      )}
      
      {currentScreen === 'matching' && (
        <UserMatching
          userProfile={{ ...userProfile, personality: personalityProfile }}
          onSelectMatch={handleSelectMatch}
          onBack={handleBackToResults}
        />
      )}
      
      {currentScreen === 'conversations' && selectedMatch && (
        <ConversationSuggestions
          userProfile={{ ...userProfile, personality: personalityProfile }}
          matchProfile={selectedMatch}
          environment={environment}
          onBack={() => setCurrentScreen('matching')}
          onEnvironmentChange={setEnvironment}
        />
      )}
    </div>
  );
};

export default ConversationEngine;